import React from 'react';
import { coverImageFor, PROJECT_PLACEHOLDER, type CoverInput, type CoverSource } from '@/lib/coverImage';
import { tradeCoverImage } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * COVER IMAGE — the ONLY <img> used to render a project cover.
 * ───────────────────────────────────────────────────────────────────────────
 * coverImageFor() guarantees a non-empty URL, but a URL can still fail at the
 * network layer (a deleted upload in storage, a CDN hiccup, an offline client).
 * A raw <img> would then paint the browser's broken-image icon — which is
 * exactly the "gray placeholder" outcome this feature exists to eliminate.
 *
 * So this component continues the SAME priority chain at runtime:
 *   1. upload            -> onError -> 2
 *   2. trade/category    -> onError -> 3
 *   3. branded fallback  -> onError -> stop (last resort, held visible)
 *
 * It also renders a tinted shimmer UNDER the image until the load event fires,
 * so a lazily-loaded card is never a blank white rectangle while scrolling.
 * The wrapper owns the aspect ratio and the image is always object-cover, so
 * nothing stretches or distorts regardless of the source photo's dimensions.
 */

interface Props {
  listing: CoverInput | null | undefined;
  /** Accessible name. Pass '' for a decorative category/fallback cover. */
  alt: string;
  className?: string;
  /** Tailwind aspect ratio utility for the wrapper. */
  aspect?: string;
  /** Above-the-fold covers should pass false to skip lazy loading. */
  lazy?: boolean;
  /** Notifies the parent which tier actually rendered (used by the audit panel). */
  onResolved?: (source: CoverSource) => void;
  children?: React.ReactNode;
}

const CoverImage: React.FC<Props> = ({
  listing,
  alt,
  className = '',
  aspect = 'aspect-[4/3]',
  lazy = true,
  onResolved,
  children,
}) => {
  const initial = coverImageFor(listing);

  const [src, setSrc] = React.useState(initial.src);
  const [source, setSource] = React.useState<CoverSource>(initial.source);
  const [loaded, setLoaded] = React.useState(false);

  // Re-resolve when the row changes (e.g. the card is recycled in a new grid).
  React.useEffect(() => {
    const next = coverImageFor(listing);
    setSrc(next.src);
    setSource(next.source);
    setLoaded(false);
  }, [initial.src]);

  React.useEffect(() => {
    if (loaded) onResolved?.(source);
  }, [loaded, source, onResolved]);

  /** Runtime degradation: upload -> trade -> branded fallback. Never a broken icon. */
  const handleError = () => {
    if (source === 'upload') {
      const trade = tradeCoverImage(listing?.trade) || tradeCoverImage(listing?.category);
      if (trade && trade !== src) {
        setSource('trade');
        setSrc(trade);
        return;
      }
    }
    if (source !== 'fallback' && src !== PROJECT_PLACEHOLDER) {
      setSource('fallback');
      setSrc(PROJECT_PLACEHOLDER);
    }
  };

  return (
    <div className={`relative w-full overflow-hidden bg-slate-200 ${aspect} ${className}`}>
      {/* Shimmer beneath the photo — prevents a blank area during lazy load. */}
      {!loaded && (
        <span
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-gradient-to-br from-slate-200 via-slate-100 to-slate-200"
        />
      )}
      <img
        src={src}
        alt={alt}
        aria-hidden={alt ? undefined : true}
        loading={lazy ? 'lazy' : 'eager'}
        decoding="async"
        fetchPriority={lazy ? 'auto' : 'high'}
        data-cover-source={source}
        onLoad={() => setLoaded(true)}
        onError={handleError}
        className={`relative h-full w-full object-cover object-center transition-opacity duration-300 ${
          loaded ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {children}
    </div>
  );
};

export default CoverImage;
